import type { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { TranslationFnc } from '~/_shared/utils/hooks';
import { roundNumberDecimalPlaces } from '~/_shared/utils/number/number.helpers';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { convertMetersToUnit } from '~/_shared/utils/unitSystem/unitSystem.helpers';
import type { PlaceDetail } from '~/places/places.repository';
import type { PlaceDetailItemProps } from './placeDetailItem.component';

const MAX_PRICE_LEVEL = 4;
const DISTANCE_DECIMAL_PLACES = 1;
const DISTANCE_SHOW_DECIMALS_UP_TO = 10;

export type PlaceDetailRow = PlaceDetailItemProps & Readonly<{ id: string }>;

const toAbsoluteUrl = (url: string) => (
  /^https?:\/\//i.test(url) ? url : `//${url}`
);

const joinNonEmpty = (values: ReadonlyArray<string> | undefined) => {
  const nonEmpty = values?.filter(value => value.trim().length > 0) ?? [];
  return nonEmpty.length ? nonEmpty.join(', ') : undefined;
};

export const getPlaceDetailRows = ({ placeDetail, distanceUnit, t }: {
  placeDetail: PlaceDetail;
  distanceUnit: UnitSystem;
  t: TranslationFnc;
}): ReadonlyArray<PlaceDetailRow> => {
  const {
    categories, chains, dateClosed, description, distance, email,
    menu, phone, popularity, priceLevel, rating, tastes, website,
  } = placeDetail;

  const rows: Array<PlaceDetailRow | null> = [
    toRow('description', description, t),
    toRow('categories', joinNonEmpty(categories), t),
    toRow('rating', rating ? `${roundNumberDecimalPlaces(rating.value, 1)} / ${rating.max}` : undefined, t),
    toRow('priceLevel', priceLevel ? '$'.repeat(Math.min(priceLevel, MAX_PRICE_LEVEL)) : undefined, t),
    toRow('popularity', notNullsy(popularity) ? `${Math.round(popularity * 100)}%` : undefined, t),
    toRow('phone', phone, t, phone ? `tel:${phone}` : undefined),
    toRow('website', website, t, website ? toAbsoluteUrl(website) : undefined),
    toRow('email', email, t, email ? `mailto:${email}` : undefined),
    toRow('menu', menu, t, menu ? toAbsoluteUrl(menu) : undefined),
    toRow('tastes', joinNonEmpty(tastes), t),
    toRow('chains', joinNonEmpty(chains?.map(chain => chain.name)), t),
    toRow('distance', notNullsy(distance)
      ? `${roundNumberDecimalPlaces(convertMetersToUnit(distance, distanceUnit), DISTANCE_DECIMAL_PLACES, DISTANCE_SHOW_DECIMALS_UP_TO)}${t(distanceUnit)}`
      : undefined, t),
    toRow('dateClosed', dateClosed, t),
  ];

  return rows.filter(notNullsy);
};

// businessStatus, link and storeId are deliberately left out for now, their translations are already in place
type PlaceDetailAttribute = 'categories' | 'chains' | 'dateClosed' | 'description' | 'distance'
  | 'email' | 'menu' | 'phone' | 'popularity' | 'priceLevel' | 'rating' | 'tastes' | 'website';

// attributes the provider has no value for are left out of the list entirely
const toRow = (
  attribute: PlaceDetailAttribute,
  value: string | undefined,
  t: TranslationFnc,
  href?: string,
): PlaceDetailRow | null => {
  if (!value?.trim()) {
    return null;
  }

  return {
    id: attribute,
    label: t(`placesTool.place.detail.attribute.${attribute}`),
    value,
    href,
  };
};
